import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Typography, Grid,    
    Box
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import PageHeader from "components/Header/Page";
import { AlertDetailView } from "./AlertDetailView";
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import moment from "moment";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteConfirmationDialog from "./DeleteModal";
import EditIcon from '@mui/icons-material/Edit';
import '../jobExecutions/jobExecutions.css';
import '../jobs/jobs.css';
import './alert-kpi.scss';
import { getConfigDetails, getConfigAlerts, deleteAlertConfig } from 'service/service';
import { usePepwiseAuthController } from "Context/contexts/AuthContext";
import { checkRole } from "utils/roleUtils";
import DateTimeRangeFilter from 'components/Dropdown/DateTimeRangeFilter/DateTimeRangeFilter';
import PageWrapper from "components/Common/PageWraper";
import { useHeader } from "Context/contexts/HeaderTabRightContentContext";
import WidgetMoreActionMenu from "components/Dropdown/WidgetMoreActionMenu";

const ALERT_DATE_FIXED_OPTIONS = [
    { val: '15d', label: 'Last 15 Days' },
    { val: 'this_month', label: 'This Month' },
    { val: 'last_month', label: 'Last Month' },
    { val: 'last_3_months', label: 'Last 3 Months' },
    { val: '180d', label: 'Last 6 Months' },
    { val: '270d', label: 'Last 9 Months' },
    { val: 'this_year', label: 'This Year' },
    { val: 'last_year', label: 'Last Year' },
    { val: 'last_2_years', label: 'Last 2 Years' }
];

const CardItem = ({label, text}: any) => {
    const theme = useTheme();
    const colors = theme.palette;

    return (
        <Grid item xs style={{ cursor: 'pointer', paddingLeft: 8, marginLeft: 8 }}>
            <Box style={{ height: '100%', display: 'flex', flexDirection: 'column'}}>
                <Typography
                    variant="bodyText"
                    sx={{
                        opacity: 0.7,
                        pr: 2,
                        color: colors.widget.text.title
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    variant="bodyText"
                    sx={{
                        color: colors.widget.text.value
                    }}
                >
                    {text}
                </Typography>
            </Box>
        </Grid>
    )
}

const Card = ({label, text, isActive = false}: any) => {
    const theme = useTheme();
    const colors = theme.palette;

    return (
        <Grid
            item
            xs
            style={{
                cursor: 'pointer',
                paddingLeft: 8,
                marginLeft: 8,
                backgroundColor: isActive ? colors.primary.main : colors.widget.background.card,
                borderRadius: '5px'
            }}
        >
            <Box style={{ height: '100%', display: 'flex', flexDirection: 'column'}}>
                <Typography
                    variant="bodyText"
                    sx={{
                        pr: 2,
                        color: isActive ? colors.common.white : colors.widget.text.title
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    variant="bodyText"
                    sx={{
                        color: isActive ? colors.textLight.light : colors.widget.text.value,
                        pt: 2,
                        fontWeight: 600
                    }}
                >
                    {text}
                </Typography>
            </Box>
        </Grid>
    )
}

const AlertDetails = () => {
    const theme = useTheme();
    const colors = theme.palette;
    const { setRightContextContent } = useHeader();
    const { id } = useParams();
    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10)
    const [metricsData, setMetricsData] = useState<any>({})
    const navigate = useNavigate()
    const initStartDate = moment().tz('America/New_York').subtract(15, 'days').toDate()
    const initEndDate = moment().tz('America/New_York').toDate()
    const [fromDateTime, setFromDateTime] = useState<any>(initStartDate);
    const [toDateTime, setToDateTime] = useState<any>(initEndDate)
    const [dateRangeType, setDateRangeType] = useState<string>('15d');
    const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
    const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false)
    const [alertList, setAlertList] = useState([])
    const [alertConfigDetails, setAlertConfigDetails] = useState<AlertConfig | undefined>({
        program: '',
        _id: '',
        alertType: '',
        ruleName: ''
    });

    const [sortingInfo, setSortingInfo] = useState({
        sortColumn: "",
        sortDir: "",
    });
    const [filterInfo, setFilterInfo] = useState([]);
    const [filterData, setFilterData] = useState<any[]>([]);
    
    const defaultBody = { page: 1, pageSize: 10, sortColumn: sortingInfo.sortColumn, sortDir: sortingInfo.sortDir, filters: filterInfo }

    const defaultFilterOptions = {
        email: ["Yes", "No"],
        teams: ["Yes", "No"],
        snow: ["Yes", "No"],
        grouped: ["Yes", "No"],
        silenced: ["Email", "Service Now", "Teams", "None"]
    };

    const [controller] = usePepwiseAuthController();
	const { role } = controller;
    const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);

    const handleDeleteClick = () => {
        setOpenDialog(true);
    };
    const handleDeleteConfirm = async () => {
        setOpenDialog(false);
        await deleteAlertConfig(alertConfigDetails?._id)
        window.location.href = '/alertFramework/alert';
    };

    interface AlertConfig {
        program?: string;
        _id?: any;
        alertType: string;
        ruleName: string;
    }

    const handleFilter = (newFilterInfo: any) => {
        setFilterInfo(newFilterInfo);
        setPage(1)
        fetchAlertList({
            page: 1,
            pageSize,
            sortColumn: sortingInfo.sortColumn,
            sortDir: sortingInfo.sortDir,
            filters: newFilterInfo
        });
    };

    const handleSorting = (newSortingInfo: any) => {
        setSortingInfo(newSortingInfo);
        fetchAlertList({
            page,
            pageSize,
            sortColumn: newSortingInfo.sortBy,
            sortDir: newSortingInfo.sortDir,
            filters: filterInfo
        });
    };

    const handlePageSizeChange = (event: any) => {
        const newPageSize = parseInt(event.target.value);
        setPageSize(newPageSize);
        fetchAlertList({
            page,
            pageSize: newPageSize,
            sortColumn: sortingInfo.sortColumn,
            sortDir: sortingInfo.sortDir,
            filters: filterInfo
        });
    };

	const handlePageChange = (data:any, newPage:any) => {
		setPage(newPage)
		fetchAlertList({
            page: newPage, 
            pageSize,
            sortColumn: sortingInfo.sortColumn,
            sortDir: sortingInfo.sortDir,
            filters: filterInfo
        })
	}

    const handleDateRangeChange = (type: string, start?: Date, end?: Date) => {
        if (!start || !end) return;

        setDateRangeType(type);
        setFromDateTime(start);
        setToDateTime(end);
        setPage(1);

        if (type === 'custom') {
            setCustomStartDate(start);
            setCustomEndDate(end);
        } else {
            setCustomStartDate(null);
            setCustomEndDate(null);
        }

        fetchAlertGridConfig(start, end)
        fetchAlertList({
            page: 1,
            pageSize,
            sortColumn: sortingInfo.sortColumn,
            sortDir: sortingInfo.sortDir,
            filters: filterInfo
        }, false, start, end)
    }

    const fetchAlertList = async (
            { page, pageSize, sortColumn, sortDir, filters = {} }: any,
            clearTimeFilter = false,
            startDate = fromDateTime,
            endDate = toDateTime
        ) => {

        const filterHandlers: any = {
            email: (fil: any[], filterObj: any) => {
            if (fil.length === 1) {
                filterObj.emailTime =
                fil[0] === "Yes" ? { $ne: null } : { $eq: null };
            }
            },

            snow: (fil: any[], filterObj: any) => {
            if (fil.length === 1) {
                filterObj.serviceNowTime =
                fil[0] === "Yes" ? { $ne: null } : { $eq: null };
            }
            },

            teams: (fil: any[], filterObj: any) => {
            if (fil.length === 1) {
                filterObj.msTeamsTime =
                fil[0] === "Yes" ? { $ne: null } : { $eq: null };
            }
            },

            silenced: (fil: any[], filterObj: any) => {
            if (fil.length > 0) {
                filterObj.silencedChannels = { $in: fil };
            }
            },

            grouped: (fil: any[], filterObj: any) => {
            if (fil.length === 1) {
                filterObj.isGrouped = fil[0] === "Yes" ? true : null;
            }
            }
        };

        const filter = Object.keys(filters).reduce(
            (filterObj: any, key: string) => {
            const handler = filterHandlers[key];
            if (handler) {
                handler(filters[key], filterObj);
            }
            return filterObj;
            },
            {
            startDate: clearTimeFilter ? initStartDate : startDate,
            endDate: clearTimeFilter ? initEndDate : endDate
            }
        );

        setLoading(true);

        try {
            const response: any = await getConfigAlerts(id, {
            page,
            pageSize,
            sortColumn: sortColumn === "createdDt" ? "createdAt" : sortColumn,
            sortDir,
            filter
            });

            if (response?.data) {
            const transformedData: any = response.data.data.map((alert: any) => ({
                ...alert,
                _id:
                typeof alert._id === "object" &&
                alert._id !== null &&
                alert._id.$oid
                    ? alert._id.$oid
                    : alert._id,
                email: alert.emailTime ? "Yes" : "No",
                snow: alert.serviceNowTime ? "Yes" : "No",
                teams: alert.msTeamsTime ? "Yes" : "No",
                silenced:
                alert.silencedChannels && alert.silencedChannels.length > 0
                    ? alert.silencedChannels.join(", ")
                    : "None",
                grouped: alert.isGrouped ? "Yes" : "No",
                createdDt: moment(alert.createdAt)
                .utc()
                .format("YYYY-MM-DD HH:mm:ss")
            }));

            setAlertList(transformedData);
            setTotalCount(response.data.totalCount);
            setFilterData(response.data.filterOptions ?? defaultFilterOptions);
            }
        } catch (error) {
            console.error("Error fetching alerts:", error);
        } finally {
            setLoading(false);
        }
        };


    const fetchAlertGridConfig = async (startDate = fromDateTime, endDate = toDateTime) => {
        setLoading(true)
        const response = await getConfigDetails(id, {filter: {startDate: startDate, endDate: endDate}})
        setLoading(false)
        if (response?.data) {
            setAlertConfigDetails(response.data.alertConfig)
            setMetricsData(response.data.counts)
        }
    }

    useEffect(() => {
        fetchAlertList(defaultBody)
        fetchAlertGridConfig()
    }, [])

    useEffect(() => {
        setRightContextContent(
            <DateTimeRangeFilter
                dateRangeType={dateRangeType}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onChange={handleDateRangeChange}
                timezone="America/New_York"
                fixedOptions={ALERT_DATE_FIXED_OPTIONS}
            />
        );

        return () => {
            setRightContextContent(null);
        };
    }, [dateRangeType, customStartDate, customEndDate, filterInfo, sortingInfo, pageSize]);

    return (
            <PageWrapper>
                <PageHeader title={[{text: "Alerts", link: '/alertFramework/alert'}, {text:`${alertConfigDetails?.ruleName}`}]} icon={DashboardIcon}>
                </PageHeader>
            
            <Grid container spacing={1} style={{ paddingRight: 2, paddingLeft: 2, marginTop: '0', display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', borderRadius: '4px'}}>
                <Grid xs={8} container spacing={1} style={{ marginTop: '0', display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', backgroundColor: colors.widget.background.card }}>
                    <CardItem label={'Name'} text={alertConfigDetails?.ruleName}/>
                    <CardItem label={'Program'} text={alertConfigDetails?.program}/>
                    <CardItem label={'Alert Type'} text={alertConfigDetails?.alertType}/>
                    <Grid item xs={1} style={{ cursor: 'pointer', textAlign: 'right' }}>
                        <Box>
                            <WidgetMoreActionMenu
                                menuItems={[
                                    {
                                        key: 'edit',
                                        label: 'Edit',
                                        icon: <EditIcon fontSize="small" sx={{ color: `${colors.backgroundLight.icon} !important` }} />,
                                        disabled: !isAllowed,
                                        onMenuClick: () => {
                                            navigate(`/alertFramework/${id}`)
                                        }
                                    },
                                    {
                                        key: 'delete',
                                        label: 'Delete',
                                        icon: <DeleteOutlineIcon fontSize="small" sx={{ color: `${colors.moreOptionDropdown.deleteMenuItem} !important` }} />,
                                        disabled: !isAllowed,
                                        onMenuClick: () => {
                                            handleDeleteClick()
                                        }
                                    }
                                ]}
                            />
                            <DeleteConfirmationDialog
                                open={openDialog}
                                onClose={() => setOpenDialog(false)}
                                onConfirm={handleDeleteConfirm}
                                identifierName={alertConfigDetails?.ruleName || ""}
                                type={''}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Grid container spacing={1} style={{ marginLeft: 1, marginTop: '0', display: 'flex', flexWrap: 'nowrap', overflowX: 'auto' }}>
                    <Card label={'Alerts Generated'} text={metricsData.totalCount} isActive/>
                    <Card label={'Email Alerts'} text={metricsData.emailsDelivered}/>
                    <Card label={'SNOW Alerts'} text={metricsData.snowDelivered}/>
                    <Card label={'Teams Alerts'} text={metricsData.teamsDelivered}/>
                    <Card label={'Grouped Alerts'} text={metricsData.grouped || 0}/>
                </Grid>
            </Grid>
            <AlertDetailView
                alertList={alertList}
                setAlertList={setAlertList}
                handleSorting={handleSorting}
                sortingInfo={sortingInfo}
                handleFilter={handleFilter}
                filterInfo={filterInfo}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                handlepageSizeChange={handlePageSizeChange}
                totalCount={totalCount}
                fetchAlertList={fetchAlertList}
                filterData={filterData}
                handlePageChange={handlePageChange}
                loading={loading}
            />
        </PageWrapper>
    );
};

export default AlertDetails;
